import * as React from "react"
import { Link, router } from "@inertiajs/react"
import { Check, Pencil, Plus, Search, Trash2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

export type ConversationSummary = {
  id: number
  title: string
  preview: string
  last_message_at: string
  message_count: number
}

// Absolute, deterministic (SSR-safe) short stamp — no "2 jam lalu" relative time, which
// would differ between server render and client hydration.
function fmtStamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(d)
}

const CONVERSATIONS_PATH = "/admin/assistant/conversations"

// Wrap every case-insensitive occurrence of `query` in `text` with <mark> so the matched
// substring in a title/snippet is visible. Pure string work — SSR-safe.
function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim()
  if (!q) return <>{text}</>
  const lower = text.toLowerCase()
  const needle = q.toLowerCase()
  const parts: React.ReactNode[] = []
  let i = 0
  let key = 0
  for (;;) {
    const idx = lower.indexOf(needle, i)
    if (idx < 0) {
      parts.push(text.slice(i))
      break
    }
    if (idx > i) parts.push(text.slice(i, idx))
    parts.push(
      <mark key={key++} className="rounded bg-accent/20 px-0.5 text-ink-display">
        {text.slice(idx, idx + q.length)}
      </mark>,
    )
    i = idx + q.length
  }
  return <>{parts}</>
}

function ConversationRow({
  conversation,
  active,
  query,
  onOpen,
  onChanged,
  // Roving-tabindex (#54 M7): only the "current" row is in the Tab sequence — its open
  // button and its rename/archive actions are tabbable; every other row is tabIndex -1 and
  // reached with ↑/↓/Home/End instead. `registerRef` lets the list focus this row's open
  // button on an arrow key; `onFocusRow` syncs the roving index when the row is focused.
  tabbable,
  registerRef,
  onFocusRow,
}: {
  conversation: ConversationSummary
  active: boolean
  query: string
  onOpen: () => void
  onChanged?: () => void
  tabbable: boolean
  registerRef: (el: HTMLButtonElement | null) => void
  onFocusRow: () => void
}) {
  const [editing, setEditing] = React.useState(false)
  const [title, setTitle] = React.useState(conversation.title)
  const rove = tabbable ? 0 : -1

  React.useEffect(() => setTitle(conversation.title), [conversation.title])

  const saveTitle = () => {
    const next = title.trim()
    if (!next || next === conversation.title) {
      setEditing(false)
      setTitle(conversation.title)
      return
    }
    router.patch(
      `${CONVERSATIONS_PATH}/${conversation.id}`,
      { conversation: { title: next } },
      { preserveScroll: true, onFinish: () => setEditing(false), onSuccess: () => onChanged?.() },
    )
  }

  const archive = () => {
    if (window.confirm("Arsipkan percakapan ini?")) {
      router.delete(`${CONVERSATIONS_PATH}/${conversation.id}`, { preserveScroll: true, onSuccess: () => onChanged?.() })
    }
  }

  if (editing) {
    return (
      <div role="listitem" className="flex items-center gap-1 rounded-lg border border-accent/40 bg-surface px-2 py-1.5">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveTitle()
            if (e.key === "Escape") {
              setEditing(false)
              setTitle(conversation.title)
            }
          }}
          aria-label="Judul percakapan"
          autoFocus
          className="h-8"
        />
        <Button variant="ghost" size="sm" onClick={saveTitle} aria-label="Simpan judul">
          <Check className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditing(false)
            setTitle(conversation.title)
          }}
          aria-label="Batal"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div
      role="listitem"
      className={cn(
        // #54 M7 — the active tint is kept light enough that the muted subtitle/timestamp on
        // it still clear WCAG AA (bg-accent/10 dropped ink-muted to 4.34:1 in light mode); the
        // accent border remains the primary active cue.
        "group flex items-start gap-2 rounded-lg border px-3 py-2 transition-colors",
        active ? "border-accent/40 bg-accent/5" : "border-transparent hover:bg-surface",
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        onFocus={onFocusRow}
        ref={registerRef}
        tabIndex={rove}
        className="min-w-0 flex-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-page"
        aria-current={active ? "true" : undefined}
      >
        <div className="truncate text-sm font-medium text-ink-display">
          <Highlight text={conversation.title} query={query} />
        </div>
        <div className="truncate text-xs text-ink-muted">
          {conversation.preview ? <Highlight text={conversation.preview} query={query} /> : "Belum ada pesan"}
        </div>
        <div className="mt-0.5 text-[0.7rem] tabular-nums text-ink-muted">{fmtStamp(conversation.last_message_at)}</div>
      </button>
      <div className="flex shrink-0 flex-col gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <Button variant="ghost" size="sm" tabIndex={rove} onClick={() => setEditing(true)} aria-label="Ganti judul" title="Ganti judul">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" tabIndex={rove} onClick={archive} aria-label="Arsipkan" title="Arsipkan">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

export function ConversationSidebar({
  conversations,
  activeId,
  onNavigate,
  onChanged,
  className,
}: {
  conversations: ConversationSummary[]
  activeId: number | null
  onNavigate?: () => void
  // #54 M6 — notify the page a conversation was created/renamed/archived so it can tell
  // peer tabs (BroadcastChannel) to refresh their sidebars.
  onChanged?: () => void
  className?: string
}) {
  const [query, setQuery] = React.useState("")

  // Ranked search over the already-loaded rows (no new endpoint): a title hit outranks a
  // preview-only hit, an earlier match position ranks higher, ties break by recency. Empty
  // query → the natural (server) order.
  const results = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return conversations
    const scored = conversations
      .map((c) => {
        const titleIdx = c.title.toLowerCase().indexOf(q)
        const previewIdx = c.preview.toLowerCase().indexOf(q)
        if (titleIdx < 0 && previewIdx < 0) return null
        const score = titleIdx >= 0 ? 1000 - titleIdx : 500 - previewIdx
        return { c, score }
      })
      .filter((x): x is { c: ConversationSummary; score: number } => x !== null)
    scored.sort(
      (a, b) =>
        b.score - a.score ||
        (a.c.last_message_at < b.c.last_message_at ? 1 : a.c.last_message_at > b.c.last_message_at ? -1 : 0),
    )
    return scored.map((s) => s.c)
  }, [conversations, query])

  // Roving tabindex (#54 M7): the conversation list is a single Tab stop. ↑/↓/Home/End move
  // between rows; Enter/Space (native <button>) opens; the "current" row's rename/archive
  // are the only actions in the Tab order. The roving index lands on the active conversation
  // so Tab enters the list at the thread that's already open. Recomputed when the (filtered)
  // list or the active id changes — never on the arrow keys themselves (results is memoized),
  // so keyboard navigation isn't reset out from under the user.
  const openRefs = React.useRef<(HTMLButtonElement | null)[]>([])
  const [focusIdx, setFocusIdx] = React.useState(0)
  React.useEffect(() => {
    const activeIdx = results.findIndex((c) => c.id === activeId)
    setFocusIdx((i) => (activeIdx >= 0 ? activeIdx : Math.min(Math.max(i, 0), Math.max(results.length - 1, 0))))
  }, [results, activeId])

  const onListKeyDown = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName
    if (tag === "INPUT" || tag === "TEXTAREA") return // renaming inline — let the field own its keys
    if (results.length === 0) return
    let next = focusIdx
    switch (e.key) {
      case "ArrowDown":
        next = Math.min(focusIdx + 1, results.length - 1)
        break
      case "ArrowUp":
        next = Math.max(focusIdx - 1, 0)
        break
      case "Home":
        next = 0
        break
      case "End":
        next = results.length - 1
        break
      default:
        return
    }
    e.preventDefault()
    setFocusIdx(next)
    openRefs.current[next]?.focus()
  }

  const newChat = () => {
    router.post(
      CONVERSATIONS_PATH,
      {},
      {
        preserveScroll: true,
        onSuccess: () => {
          onNavigate?.()
          onChanged?.()
        },
      },
    )
  }

  const open = (id: number) => {
    router.visit(`/admin/assistant/${id}`, { onSuccess: () => onNavigate?.() })
  }

  return (
    <div className={cn("flex h-full flex-col gap-3", className)}>
      <Button onClick={newChat} className="w-full justify-center">
        <Plus className="h-4 w-4" />
        <span className="ml-1">Chat baru</span>
      </Button>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" aria-hidden="true" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cari percakapan…"
          aria-label="Cari percakapan"
          className="pl-8 pr-8"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Bersihkan pencarian"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-ink-muted hover:text-ink-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div
        role="list"
        aria-label="Daftar percakapan"
        onKeyDown={onListKeyDown}
        className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1"
      >
        {results.length === 0 ? (
          <p className="px-3 py-4 text-sm text-ink-muted">
            {conversations.length === 0 ? "Belum ada percakapan." : `Tidak ada hasil untuk "${query.trim()}".`}
          </p>
        ) : (
          results.map((c, i) => (
            <ConversationRow
              key={c.id}
              conversation={c}
              active={c.id === activeId}
              query={query}
              onOpen={() => open(c.id)}
              onChanged={onChanged}
              tabbable={i === focusIdx}
              registerRef={(el) => {
                openRefs.current[i] = el
              }}
              onFocusRow={() => setFocusIdx(i)}
            />
          ))
        )}
      </div>
    </div>
  )
}

// A no-JS / progressive fallback isn't needed (admin-gated SPA), but keep a Link export
// for any future server-rendered listing.
export function ConversationLink({ conversation }: { conversation: ConversationSummary }) {
  return <Link href={`/admin/assistant/${conversation.id}`}>{conversation.title}</Link>
}
